import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  userId: string;
  apitoken: string;
  roleId: number;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

// Initialize auth state from localStorage
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async () => {
    const token = localStorage.getItem('accessToken');
    const isLoggedIn = localStorage.getItem('isLoggedin');
    const userDetails = localStorage.getItem('userDetails');
    
    if (token && isLoggedIn === 'true' && userDetails) {
      try {
        const user = JSON.parse(userDetails);
        return { user, isAuthenticated: true };
      } catch (error) {
        console.error('Error parsing user details from localStorage:', error);
        return { user: null, isAuthenticated: false };
      }
    }
    
    return { user: null, isAuthenticated: false };
  }
);

// Login async thunk - simplified flow
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { dispatch }) => {
    try {
      // Call login API
      const loginResponse = await authAPI.login(email, password);
      const user = loginResponse.data;

      // Store user data in localStorage
      localStorage.setItem("accessToken", user.apitoken);
      localStorage.setItem("isLoggedin", "true");
      localStorage.setItem("userDetails", JSON.stringify(user));

      // Return user
      return { user };
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Login failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    // Clear all localStorage items (same as Angular version)
    localStorage.removeItem('accessToken');
    localStorage.removeItem('isLoggedin');
    localStorage.removeItem('userDetails');
    localStorage.removeItem('userId');
    localStorage.removeItem('Setting');
    localStorage.removeItem('version');
    localStorage.removeItem('codeId');
    localStorage.removeItem('userLoggedBy');
    localStorage.removeItem('userLoggedByCode');
    localStorage.removeItem('merchantPanelType');
    
    return null;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.loading = false;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      });
  },
});

export const { clearError, setUser } = authSlice.actions;
export default authSlice.reducer; 